import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';

/**
 * PIN-style time input — 4 fixed digit places with colon separator.
 * Type digits like a passcode: 0, 8, 3, 0 → 08:30
 * Backspace clears right-to-left. Click to focus. No cursor visible.
 *
 * @param {number} value - Minutes from midnight (e.g. 510 = 08:30)
 * @param {function} onChange - Called with minutes when all 4 digits entered
 * @param {function} onComplete - Called after 4th digit committed (parent can move focus)
 * @param {function} onNavigate - Called with (direction, digitPosition) when arrow keys hit boundaries
 *   direction: 'next' (right past last digit), 'prev' (left past first digit), 'up', 'down'
 * @param {number} min - Minimum valid time in minutes (exclusive). Rejects & flashes red if result ≤ min.
 * @param {boolean} readOnly - Display only, no interaction
 * @param {string} className - Optional wrapper class
 */
const TimeInput = forwardRef(({ value = 0, onChange, onComplete, onNavigate, min, readOnly = false, className = '' }, ref) => {
  const minutesToDigits = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return [
      Math.floor(h / 10),
      h % 10,
      Math.floor(m / 10),
      m % 10
    ];
  };

  const [digits, setDigits] = useState(() => minutesToDigits(value));
  const [activeIndex, setActiveIndex] = useState(-1); // -1 = not focused
  const [rejected, setRejected] = useState(false);
  const containerRef = useRef(null);
  const hiddenInputRef = useRef(null);
  const pendingPositionRef = useRef(0);
  const committedRef = useRef(false); // true when 4th digit already called onChange

  // Expose focus(position) to parent via ref
  useImperativeHandle(ref, () => ({
    focus: (position = 0) => {
      if (readOnly) return;
      pendingPositionRef.current = Math.min(3, Math.max(0, position));
      hiddenInputRef.current?.focus();
    },
  }));

  // Sync from parent value when not actively editing
  useEffect(() => {
    if (activeIndex === -1) {
      setDigits(minutesToDigits(value));
    }
  }, [value, activeIndex]);

  const digitsToMinutes = useCallback((d) => {
    const h = d[0] * 10 + d[1];
    const m = d[2] * 10 + d[3];
    return h * 60 + m;
  }, []);

  const isValidTime = useCallback((d) => {
    const h = d[0] * 10 + d[1];
    const m = d[2] * 10 + d[3];
    return h < 24 && m < 60;
  }, []);

  const handleFocus = () => {
    if (readOnly) return;
    pendingPositionRef.current = 0;
    hiddenInputRef.current?.focus();
  };

  const handleBlur = () => {
    setActiveIndex(-1);
    // Skip commit if 4th digit already called onChange (stale closure would overwrite)
    if (committedRef.current) {
      committedRef.current = false;
      return;
    }
    // Commit on blur if valid (user tabbed/clicked away mid-edit)
    if (isValidTime(digits)) {
      const mins = digitsToMinutes(digits);
      if (mins !== value) onChange(mins);
    } else {
      // Revert to previous value
      setDigits(minutesToDigits(value));
    }
  };

  const handleKeyDown = (e) => {
    e.preventDefault();
    const key = e.key;

    if (key === 'Backspace') {
      const newDigits = [...digits];
      if (activeIndex > 0) {
        const prevIndex = activeIndex - 1;
        newDigits[prevIndex] = 0;
        setDigits(newDigits);
        setActiveIndex(prevIndex);
      } else {
        newDigits[0] = 0;
        setDigits(newDigits);
      }
      return;
    }

    if (key === 'Tab') {
      hiddenInputRef.current?.blur();
      return;
    }

    if (key === 'Escape') {
      setDigits(minutesToDigits(value));
      hiddenInputRef.current?.blur();
      return;
    }

    // Arrow keys — navigate within digits or signal parent
    if (key === 'ArrowRight') {
      if (activeIndex < 3) {
        setActiveIndex(activeIndex + 1);
      } else if (onNavigate) {
        onNavigate('next', activeIndex);
      }
      return;
    }
    if (key === 'ArrowLeft') {
      if (activeIndex > 0) {
        setActiveIndex(activeIndex - 1);
      } else if (onNavigate) {
        onNavigate('prev', activeIndex);
      }
      return;
    }
    if (key === 'ArrowUp') {
      if (onNavigate) onNavigate('up', activeIndex);
      return;
    }
    if (key === 'ArrowDown') {
      if (onNavigate) onNavigate('down', activeIndex);
      return;
    }

    // Only accept digits
    if (!/^[0-9]$/.test(key)) return;

    const digit = parseInt(key);
    const newDigits = [...digits];
    newDigits[activeIndex] = digit;

    // Validate as we go — constrain hours digit 0 to 0-2, etc.
    const h0 = newDigits[0];

    if (activeIndex === 0 && digit > 2) return;
    if (activeIndex === 1 && h0 === 2 && digit > 3) return;
    if (activeIndex === 2 && digit > 5) return;

    setDigits(newDigits);

    if (activeIndex < 3) {
      setActiveIndex(activeIndex + 1);
    } else {
      // All 4 entered — validate and commit
      const mins = digitsToMinutes(newDigits);

      if (isValidTime(newDigits) && min != null && mins <= min) {
        // Time ≤ minimum — flash red, revert, stay focused
        setRejected(true);
        setTimeout(() => {
          setRejected(false);
          setDigits(minutesToDigits(value));
          setActiveIndex(0);
        }, 600);
        return;
      }

      if (isValidTime(newDigits)) {
        onChange(mins);
      }
      // Flag so handleBlur doesn't re-commit with stale digits
      committedRef.current = true;
      // Let parent chain focus, or blur if no handler
      if (onComplete) {
        hiddenInputRef.current?.blur();
        onComplete();
      } else {
        hiddenInputRef.current?.blur();
      }
    }
  };

  const isFocused = activeIndex >= 0;

  return (
    <div
      ref={containerRef}
      className={`inline-flex items-center gap-0.5 select-none ${readOnly ? 'cursor-default' : 'cursor-text'} ${className}`}
      onClick={handleFocus}
      role="textbox"
      aria-label="Time input"
      tabIndex={-1}
    >
      {/* Hidden input to capture keyboard events */}
      {!readOnly && (
        <input
          ref={hiddenInputRef}
          className="sr-only"
          onFocus={() => { setActiveIndex(pendingPositionRef.current); pendingPositionRef.current = 0; }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          aria-hidden="true"
          readOnly
        />
      )}

      {/* Digit boxes */}
      {[0, 1].map(i => (
        <div
          key={i}
          className={`w-8 h-9 rounded flex items-center justify-center font-mono text-lg font-semibold transition-colors
            ${rejected
              ? 'bg-error/20 text-error'
              : isFocused && activeIndex === i
                ? 'bg-primary text-primary-content'
                : readOnly
                  ? 'bg-base-200/50 text-base-content/50'
                  : 'bg-base-300/60 text-blue-600'
            }`}
        >
          {digits[i]}
        </div>
      ))}

      <span className={`font-bold mx-0.5 text-lg transition-colors ${rejected ? 'text-error/40' : readOnly ? 'text-base-content/25' : 'text-blue-600/40'}`}>:</span>

      {[2, 3].map(i => (
        <div
          key={i}
          className={`w-8 h-9 rounded flex items-center justify-center font-mono text-lg font-semibold transition-colors
            ${rejected
              ? 'bg-error/20 text-error'
              : isFocused && activeIndex === i
                ? 'bg-primary text-primary-content'
                : readOnly
                  ? 'bg-base-200/50 text-base-content/50'
                  : 'bg-base-300/60 text-blue-600'
            }`}
        >
          {digits[i]}
        </div>
      ))}
    </div>
  );
});

TimeInput.displayName = 'TimeInput';

export default TimeInput;
